import axios from 'axios';
import { config } from '../config/environments';

const API_BASE_URL = config.API_URL;

// Log para debugging - mostrar qué entorno estamos usando
console.log('🌍 Entorno actual:', config.NAME);
console.log('🔗 API URL:', API_BASE_URL);

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain',
    },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Debug: mostrar la URL completa que se está llamando
    console.log('🌐 API Request:', config.method?.toUpperCase(), config.baseURL + config.url);

    return config;
});

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
    (response) => {
        console.log('✅ API Response:', response.config.method?.toUpperCase(), response.config.url, response.status);
        console.log('📦 Response Data:', response.data);
        return response;
    },
    (error) => {
        console.error('❌ API Error:', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status);
        console.error('📋 Error Details:', error.response?.data);

        // Verificar si es un error de red (backend no disponible)
        if (!error.response) {
            console.error('🌐 Error de red: Backend no disponible');
            // No redirigir automáticamente en errores de red
            return Promise.reject(error);
        }

        if (error.response?.status === 401) {
            console.log('🔒 Token expirado o inválido, limpiando sesión...');
            
            // Verificar si el token realmente existe antes de limpiar
            const token = localStorage.getItem('authToken');
            if (token) {
                // Limpiar todos los datos de autenticación
                localStorage.removeItem('authToken');
                localStorage.removeItem('userRole');
                localStorage.removeItem('userData');
                localStorage.removeItem('tokenExpiry');

                // Solo redirigir si no estamos ya en la página de login
                if (window.location.pathname !== '/login') {
                    console.log('🔄 Redirigiendo al login...');
                    window.location.href = '/login';
                }
            } else {
                console.log('⚠️ Error 401 pero no hay token almacenado');
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/Auth/login', credentials),
    register: (userData) => api.post('/Auth/register', userData),
    logout: () => api.post('/Auth/logout'),
    getCurrentUser: () => api.get('/Auth/me'),
};

// Version API
export const versionAPI = {
    getVersion: () => api.get('/Version'),
};

// Certificate Test API
export const certificateAPI = {
    testCertificate: (certificateBase64) => api.post('/Certificate/information', {
        certificateBase64
    }),
    testCredential: (credentialData) => api.post('/Certificate/test-credential', credentialData),
};

// Clients API
export const clientsAPI = {
    getAll: (params = {}) => {
        const queryParams = {};

        if (params.search) queryParams.search = params.search;
        if (params.page) queryParams.page = params.page;
        if (params.pageSize) queryParams.pageSize = params.pageSize;

        return api.get('/Clients', { params: queryParams });
    },
    getById: (id) => api.get(`/Clients/${id}`),
    create: (clientData) => api.post('/Clients', clientData),
    update: (id, clientData) => api.put(`/Clients/${id}`, clientData),
    delete: (id) => api.delete(`/Clients/${id}`),
    validateCertificate: (id) => api.post(`/Clients/${id}/validate-certificate`),

    // Función de prueba para diferentes endpoints
    testEndpoints: async () => {
        const endpoints = [
            '/Clients',
            '/clients',
            '/Client',
            '/client',
            '/api/Clients',
            '/api/clients',
            '/Cliente',
            '/clientes'
        ];

        for (const endpoint of endpoints) {
            try {
                console.log(`🧪 Probando endpoint: ${endpoint}`);
                const response = await api.get(endpoint);
                console.log(`✅ ${endpoint} funciona:`, response.data);
                return { endpoint, data: response.data };
            } catch (error) {
                console.log(`❌ ${endpoint} falló:`, error.response?.status, error.response?.statusText);
            }
        }
        return null;
    }
};

// Downloads API
export const downloadsAPI = {
    getAll: (params = {}) => {
        const queryParams = {};

        if (params.clientId) queryParams.clientId = params.clientId;
        if (params.status) queryParams.status = params.status;
        if (params.fechaDesde) queryParams.fechaDesde = params.fechaDesde;
        if (params.fechaHasta) queryParams.fechaHasta = params.fechaHasta;
        if (params.page) queryParams.page = params.page;
        if (params.pageSize) queryParams.pageSize = params.pageSize;
        if (params.orderBy) queryParams.orderBy = params.orderBy;
        if (params.orderDirection) queryParams.orderDirection = params.orderDirection;

        return api.get('/Downloads', { params: queryParams });
    },
    getById: (id) => api.get(`/Downloads/${id}`),
    create: (downloadData) => api.post('/Downloads/generate', downloadData),
    getStatus: (id) => api.get(`/Downloads/${id}/status`),
    downloadPackage: (id) => api.get(`/Downloads/${id}/package`, { responseType: 'blob' }),
    cancel: (id) => api.delete(`/Downloads/${id}`),
};

// Consumos API
export const consumosAPI = {
    getAll: (params = {}) => api.get('/Consumos', { params }),
    getUserConsumptions: () => api.get('/Consumos/user'),
    getClientConsumptions: (clientId) => api.get(`/Consumos/client/${clientId}`),
    getStats: () => api.get('/Consumos/stats'),
    getByDownload: (downloadId, params = {}) => api.get(`/Consumos/by-download/${downloadId}`, { params }),
};

// Users API (para superadministrador)
export const usersAPI = {
    getAll: (params = {}) => api.get('/Users', { params }),
    getById: (id) => api.get(`/Users/${id}`),
    create: (userData) => api.post('/Users', userData),
    update: (id, userData) => api.put(`/Users/${id}`, userData),
    delete: (id) => api.delete(`/Users/${id}`),
    changeRole: (id, role) => api.put(`/Users/${id}/role`, { role }),
};

// Facturación API
export const facturacionAPI = {
    // Gestión de Clientes de Facturación
    clientes: {
        getAll: (params = {}) => {
            const queryParams = {};
            if (params.search) queryParams.search = params.search;
            if (params.page) queryParams.page = params.page;
            if (params.pageSize) queryParams.pageSize = params.pageSize;
            
            return api.get('/Facturacion/clientes', { params: queryParams });
        },

        getById: (id) => api.get(`/Facturacion/clientes/${id}`),

        create: (clientData) => api.post('/Facturacion/clientes', clientData),

        update: (id, clientData) => api.put(`/Facturacion/clientes/${id}`, clientData),

        delete: (id) => api.delete(`/Facturacion/clientes/${id}`),

        search: (query) => api.get(`/Facturacion/clientes/search?q=${encodeURIComponent(query)}`)
    },

    // Gestión de Facturas
    facturas: {
        create: (facturaData) => api.post('/Facturacion/facturas', facturaData),

        getAll: (params = {}) => {
            const queryParams = {};
            if (params.clientId) queryParams.clientId = params.clientId;
            if (params.status) queryParams.status = params.status;
            if (params.fechaDesde) queryParams.fechaDesde = params.fechaDesde;
            if (params.fechaHasta) queryParams.fechaHasta = params.fechaHasta;
            if (params.page) queryParams.page = params.page;
            if (params.pageSize) queryParams.pageSize = params.pageSize;
            
            return api.get('/Facturacion/facturas', { params: queryParams });
        },

        getById: (id) => api.get(`/Facturacion/facturas/${id}`),

        getPDF: (id) => api.get(`/Facturacion/facturas/${id}/pdf`, { responseType: 'blob' }),

        getXML: (id) => api.get(`/Facturacion/facturas/${id}/xml`, { responseType: 'blob' }),

        cancel: (id, motivo) => api.post(`/Facturacion/facturas/${id}/cancelar`, { motivo }),

        timbrar: (id) => api.post(`/Facturacion/facturas/${id}/timbrar`)
    },

    // Configuración
    configuracion: {
        get: () => api.get('/Facturacion/configuracion'),

        update: (config) => api.put('/Facturacion/configuracion', config),

        testCertificados: (certificados) => api.post('/Facturacion/configuracion/test-certificados', certificados),

        testPAC: (pacConfig) => api.post('/Facturacion/configuracion/test-pac', pacConfig)
    },

    // Catálogos SAT
    catalogos: {
        getRegimenFiscal: () => api.get('/Facturacion/catalogos/regimen-fiscal'),

        getUsoCFDI: () => api.get('/Facturacion/catalogos/uso-cfdi'),

        getMetodosPago: () => api.get('/Facturacion/catalogos/metodos-pago'),

        getFormasPago: () => api.get('/Facturacion/catalogos/formas-pago'),

        getMonedas: () => api.get('/Facturacion/catalogos/monedas'),

        getUnidades: () => api.get('/Facturacion/catalogos/unidades')
    }
};

// ===== NUEVOS SERVICIOS SEGÚN DOCUMENTACIÓN =====

// CfdiController - Operaciones principales de CFDI
export const cfdiAPI = {
    // Timbrado de CFDI
    stamp: (cfdiData) => api.post('/cfdi/stamp', cfdiData),
    
    // Cancelación de CFDI
    cancel: (cancelData) => api.post('/cfdi/cancel', cancelData),
    
    // Validación de RFC
    validateRfc: (rfc) => api.post('/cfdi/validate-rfc', { rfc }),
    
    // Consulta de estado por UUID
    getStatus: (uuid) => api.get(`/cfdi/status/${uuid}`)
};

// CfdiCreationController - Creación de CFDI con SW SDK
export const cfdiCreationAPI = {
    // Crear CFDI completo
    create: (cfdiData) => api.post('/cfdicreation/create', cfdiData),
    
    // Timbrar y generar PDF
    stampAndPdf: (cfdiData) => api.post('/cfdicreation/stamp-and-pdf', cfdiData),
    
    // Validar estructura
    validateStructure: (xmlData) => api.post('/cfdicreation/validate-structure', xmlData)
};

// AdvancedPacController - Funcionalidades PAC avanzadas
export const advancedPacAPI = {
    // Cancelación masiva
    massiveCancellation: (cancelData) => api.post('/advancedpac/massive-cancellation', cancelData),
    
    // Estado de servicios PAC
    getPacStatus: () => api.get('/advancedpac/pac-status'),
    
    // Validación en lote
    batchValidation: (cfdiList) => api.post('/advancedpac/batch-validation', cfdiList)
};

// SatPortalController - Servicios del Portal SAT
export const satPortalAPI = {
    // Autenticación FIEL
    authenticate: (fielData) => api.post('/satportal/authenticate', fielData),
    
    // Solicitar descarga masiva
    requestDownload: (downloadParams) => api.post('/satportal/request-download', downloadParams),
    
    // Verificar estado de solicitud
    checkRequest: (requestId) => api.get(`/satportal/check-request/${requestId}`),
    
    // Descargar paquete
    downloadPackage: (packageId) => api.post('/satportal/download-package', { packageId }),
    
    // Validar RFC oficial
    validateRfc: (rfc) => api.post('/satportal/validate-rfc', { rfc })
};

// CatalogsController - Catálogos SAT
export const catalogsAPI = {
    // Formas de pago
    getFormasPago: () => api.get('/catalogs/forma-pago'),
    
    // Métodos de pago
    getMetodosPago: () => api.get('/catalogs/metodo-pago'),
    
    // Monedas
    getMonedas: () => api.get('/catalogs/moneda'),
    
    // Regímenes fiscales
    getRegimenesFiscales: () => api.get('/catalogs/regimen-fiscal'),
    
    // Usos de CFDI
    getUsosCfdi: () => api.get('/catalogs/uso-cfdi'),
    
    // Actualizar catálogos
    updateCatalogs: () => api.post('/catalogs/update')
};

// ValidationController - Validaciones avanzadas
export const validationAPI = {
    // Validación XSD
    validateXsd: (xmlData) => api.post('/validation/xsd', xmlData),
    
    // Validar certificado
    validateCertificate: (certData) => api.post('/validation/certificate', certData),
    
    // Validar firma digital
    validateDigitalSignature: (xmlData) => api.post('/validation/digital-signature', xmlData),
    
    // Validar cadena de confianza
    validateChainTrust: (certChain) => api.post('/validation/chain-trust', certChain)
};

// XmlTransformController - Transformaciones XML
export const xmlTransformAPI = {
    // Transformación XSLT
    xsltTransform: (transformData) => api.post('/xmltransform/xslt/transform', transformData),
    
    // Validar esquema
    validateSchema: (schemaData) => api.post('/xmltransform/validate-schema', schemaData),
    
    // XML a objeto
    xmlToObject: (xmlData) => api.post('/xmltransform/xml-to-object', xmlData),
    
    // Objeto a XML
    objectToXml: (objectData) => api.post('/xmltransform/object-to-xml', objectData)
};

// RenderingController - Renderizado de documentos
export const renderingAPI = {
    // Renderizar a HTML
    toHtml: (renderData) => api.post('/rendering/html', renderData),
    
    // Renderizar a PDF
    toPdf: (renderData) => api.post('/rendering/pdf', renderData),
    
    // Renderizar a JSON
    toJson: (xmlData) => api.post('/rendering/json', xmlData),
    
    // Renderizar múltiples a HTML
    multipleToHtml: (cfdiList) => api.post('/rendering/multiple/html', cfdiList),
    
    // Renderizar múltiples a PDF
    multipleToPdf: (cfdiList) => api.post('/rendering/multiple/pdf', cfdiList),
    
    // Obtener plantillas
    getTemplates: () => api.get('/rendering/templates')
};

// CsfController - Consulta y validación de facturas
export const csfAPI = {
    // Consultar CFDI
    consulta: (consultaData) => api.post('/csf/consulta', consultaData),
    
    // Generar URL QR
    generateQrUrl: (cfdiData) => api.post('/csf/qr-url', cfdiData),
    
    // Consulta en lote
    consultaLote: (cfdiList) => api.post('/csf/consulta-lote', cfdiList),
    
    // Validar XML
    validarXml: (xmlData) => api.post('/csf/validar-xml', xmlData),
    
    // Información del servicio
    getInfo: () => api.get('/csf/info')
};

// BanxicoController - API de Banco de México
export const banxicoAPI = {
    // Tipo de cambio actual
    getCurrentExchangeRate: () => api.get('/banxico/exchange-rate/current'),
    
    // Tipos de cambio históricos
    getHistoricalExchangeRate: (params) => api.get('/banxico/exchange-rate/historical', { params }),
    
    // Obtener serie económica
    getSeries: (serieId) => api.get(`/banxico/series/${serieId}`),
    
    // Metadatos de serie
    getSeriesMetadata: (serieId) => api.get(`/banxico/series/${serieId}/metadata`)
};

// ExcelExportController - Exportación a Excel
export const excelExportAPI = {
    // Exportar CFDI a Excel
    exportCfdi: (exportData) => api.post('/excelexport/cfdi', exportData),
    
    // Reporte personalizado
    customReport: (reportData) => api.post('/excelexport/custom-report', reportData),
    
    // Obtener plantillas
    getTemplates: () => api.get('/excelexport/templates'),
    
    // Generar con plantilla
    generateWithTemplate: (templateId, data) => api.post(`/excelexport/template/${templateId}`, data)
};

// TransactionsController - Historial de transacciones
export const transactionsAPI = {
    // Obtener transacciones
    getAll: (params) => api.get('/transactions', { params }),
    
    // Obtener transacción específica
    getById: (id) => api.get(`/transactions/${id}`),
    
    // Resumen de transacciones
    getSummary: (params) => api.get('/transactions/summary', { params }),
    
    // Estado de cuenta
    getAccountStatement: (params) => api.get('/transactions/account-statement', { params }),
    
    // Agregar créditos
    addCredit: (creditData) => api.post('/transactions/credit', creditData)
};

// AdminController - Administración del sistema
export const adminAPI = {
    // Estado del sistema
    getSystemStatus: () => api.get('/admin/system-status'),
    
    // Reporte de uso
    getUsageReport: (params) => api.get('/admin/reports/usage', { params }),
    
    // Reporte de rendimiento
    getPerformanceReport: () => api.get('/admin/reports/performance'),
    
    // Limpieza del sistema
    cleanup: () => api.post('/admin/maintenance/cleanup'),
    
    // Obtener logs
    getLogs: (params) => api.get('/admin/logs', { params }),
    
    // Actualizar configuración
    updateConfiguration: (configData) => api.post('/admin/configuration', configData)
};

// RetencionesController - Manejo de retenciones
export const retencionesAPI = {
    // Crear comprobante de retenciones
    create: (retencionData) => api.post('/retenciones/create', retencionData),
    
    // Timbrar retenciones
    stamp: (xmlData) => api.post('/retenciones/stamp', xmlData),
    
    // Obtener por UUID
    getByUuid: (uuid) => api.get(`/retenciones/${uuid}`),
    
    // Validar retenciones
    validate: (xmlData) => api.post('/retenciones/validate', xmlData)
};

export default api;